import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography
} from "@mui/material";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LoadingButton } from "@mui/lab";
import { useLoginController } from './context/login-controller';
import { useLoginState } from './context/login-context';
import { SearchableSelect } from '../../components/searchable-select';

export function LoginComponent() {
    const {
        handleOnChange,
        toggleShowPassword,
        handleCompanyChange,
        authentication,
        handleBackButton,
        applicationData
    } = useLoginController();

    const {
        preAuthenticationRequest,
        preAuthenticationResponse,
        authenticationRequest,
        showPassword,
        loading,
        fieldInvalid
    } = useLoginState();

    return (
        <React.Fragment>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: 1,
                    minHeight: '100vh'
                }}
            >
                <Container maxWidth={'sm'}>
                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden'
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sx={{ mb: 2 }}>
                                    <Stack spacing={2} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'primary.main',
                                                boxShadow: 2
                                            }}
                                        >
                                            <LockOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
                                        </Box>
                                        <Box textAlign="center">
                                            <Typography
                                                variant="h4"
                                                component="h1"
                                                sx={{
                                                    fontWeight: 700,
                                                    mb: 0.5,
                                                    color: 'text.primary'
                                                }}
                                            >
                                                {applicationData.moduleName}
                                            </Typography>
                                            {applicationData.moduleDescription && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {applicationData.moduleDescription}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: 'text.secondary',
                                                    mt: 1
                                                }}
                                            >
                                                Entre com suas credenciais para continuar
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sx={{ display: applicationData.moduleId === 1 ? 'block' : 'none' }}
                                >
                                    <InputLabel sx={{ fontWeight: 500, mb: 1 }}>
                                        Tipo de Login
                                    </InputLabel>
                                    <Select
                                        name="select-type"
                                        value={preAuthenticationRequest?.type}
                                        error={!fieldInvalid && preAuthenticationRequest.type === undefined}
                                        fullWidth
                                        onChange={handleOnChange}
                                        disabled={preAuthenticationResponse.companies?.length > 0 || loading}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <MenuItem value={1} key={1}>Entidade</MenuItem>
                                        <MenuItem value={2} key={2}>Empresa</MenuItem>
                                    </Select>
                                </Grid>

                                <Grid item xs={12}>
                                    <InputLabel sx={{ fontWeight: 500, mb: 1 }}>
                                        E-mail ou Usuário
                                    </InputLabel>
                                    <OutlinedInput
                                        name="input-user"
                                        placeholder="Digite seu e-mail ou usuário"
                                        fullWidth
                                        error={!fieldInvalid && (preAuthenticationRequest.user === undefined || preAuthenticationRequest.user.length === 0)}
                                        value={preAuthenticationRequest?.user}
                                        disabled={preAuthenticationResponse.companies?.length > 0 || loading}
                                        onChange={handleOnChange}
                                        autoComplete="off"
                                        inputProps={{
                                            autoComplete: 'off',
                                            'data-lpignore': 'true',
                                            'data-form-type': 'other'
                                        }}
                                        sx={{ borderRadius: 2 }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <InputLabel sx={{ fontWeight: 500, mb: 1 }}>
                                        Senha
                                    </InputLabel>
                                    <OutlinedInput
                                        name="input-password"
                                        placeholder="Digite sua senha"
                                        value={preAuthenticationRequest?.password}
                                        error={!fieldInvalid && (preAuthenticationRequest.password === undefined || preAuthenticationRequest.password.length === 0)}
                                        fullWidth
                                        onChange={handleOnChange}
                                        type={showPassword ? 'text' : 'password'}
                                        disabled={preAuthenticationResponse.companies?.length > 0 || loading}
                                        autoComplete="new-password"
                                        inputProps={{
                                            autoComplete: 'new-password',
                                            'data-lpignore': 'true',
                                            'data-form-type': 'other'
                                        }}
                                        sx={{ borderRadius: 2 }}
                                        endAdornment={
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label='toggle password visibility'
                                                    onClick={toggleShowPassword}
                                                    edge="end"
                                                >
                                                    {!showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </Grid>

                                {preAuthenticationResponse.companies?.length > 0 && (
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <InputLabel sx={{ fontWeight: 500, mb: 1 }}>
                                            {preAuthenticationRequest.type === 1 ? "Entidade" : "Empresa"}
                                        </InputLabel>
                                        <SearchableSelect
                                            options={preAuthenticationResponse.companies?.map((element) => ({
                                                id: element.id,
                                                label: `${element.name} - ${element.formattedDocument}`
                                            })) || []}
                                            value={authenticationRequest?.companyId}
                                            onChange={handleCompanyChange}
                                            placeholder={`Pesquisar ${preAuthenticationRequest.type === 1 ? "entidade" : "empresa"}...`}
                                            error={!fieldInvalid && (authenticationRequest.companyId === undefined || authenticationRequest.companyId === 0)}
                                            fullWidth
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    defaultChecked={false}
                                                    name="check-remember"
                                                    onChange={handleOnChange}
                                                    disabled={preAuthenticationResponse.companies?.length > 0}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    Lembrar de mim
                                                </Typography>
                                            }
                                        />
                                        <Link
                                            href="#"
                                            underline="hover"
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            Esqueceu sua senha?
                                        </Link>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        {preAuthenticationResponse.companies?.length > 0 && (
                                            <LoadingButton
                                                loading={loading}
                                                loadingPosition="start"
                                                startIcon={<ArrowCircleLeft />}
                                                onClick={handleBackButton}
                                                variant="outlined"
                                                sx={{ borderRadius: 2, px: 3 }}
                                            >
                                                Voltar
                                            </LoadingButton>
                                        )}
                                        <Box sx={{ flex: preAuthenticationResponse.companies?.length > 0 ? '0 0 auto' : '1 1 auto', ml: 'auto' }}>
                                            <LoadingButton
                                                loading={loading}
                                                variant="contained"
                                                loadingPosition="end"
                                                endIcon={
                                                    (applicationData !== undefined && preAuthenticationRequest.type === 2) ||
                                                    (preAuthenticationResponse.companies?.length > 0 && preAuthenticationRequest.type === 1)
                                                        ? <CheckCircleIcon />
                                                        : <ArrowCircleRightIcon />
                                                }
                                                onClick={authentication}
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 4,
                                                    py: 1.5,
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {(applicationData !== undefined && preAuthenticationRequest.type === 2) ||
                                                (preAuthenticationResponse.companies?.length > 0 && preAuthenticationRequest.type === 1)
                                                    ? "Entrar"
                                                    : "Próximo"}
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </React.Fragment>
    );
}
